import { z } from 'zod';
import { api } from '@/lib/api';
import { CANVAS_WIDTH, inkPath, type WorkingContent } from './workingCanvas';

export const tutorFeedbackSchema = z.object({
  verdict: z.enum(['reasonable', 'needs_revision', 'unclear']),
  summary: z.string().min(1),
  transcription: z.array(z.string()),
  steps: z.array(
    z.object({
      step: z.number().int().positive(),
      status: z.enum(['valid', 'error', 'unclear']),
      feedback: z.string(),
    }),
  ),
  first_error: z.number().int().positive().nullable(),
  next_hint: z.string().min(1),
});
export type TutorFeedback = z.infer<typeof tutorFeedbackSchema>;
export interface WorkingTutorRequest {
  expected_version: number;
  image_base64: string;
  expected_stars: number;
  idempotency_key: string;
}
export async function workingCheckKey(id: string, version: number, image: string, attempt = 0) {
  const digest = new Uint8Array(
    await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(`working-check-v1:${id}:${version}:${attempt}:${image}`),
    ),
  );
  digest[6] = (digest[6] & 15) | 80;
  digest[8] = (digest[8] & 63) | 128;
  const hex = Array.from(digest.slice(0, 16), (byte) => byte.toString(16).padStart(2, '0')).join(
    '',
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
const responseSchema = z.object({
  canvas_id: z.string().uuid(),
  version: z.number().int().positive(),
  feedback: tutorFeedbackSchema,
  stars_charged: z.number().int().nonnegative(),
});
export async function checkWorking(id: string, request: WorkingTutorRequest) {
  const result = responseSchema.parse(
    await api<unknown>(`/study/working-canvases/${encodeURIComponent(id)}/tutor-check`, {
      method: 'POST',
      body: request,
    }),
  );
  if (result.canvas_id !== id || result.version !== request.expected_version)
    throw new Error('Tutor returned a different page version. Review your saved working again.');
  return result;
}

// Generate the entire saved ink snapshot, including writing beyond the visible viewport.
// Typed steps travel separately as text so formula characters are not rasterised or truncated.
export function renderWorkingImage(content: WorkingContent): string {
  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_WIDTH;
  let bottom = 0;
  for (const stroke of content.strokes)
    for (const point of stroke.points) bottom = Math.max(bottom, point.y + stroke.width + 32);
  canvas.height = Math.min(content.height, Math.max(600, Math.ceil(bottom)));
  const drawing = canvas.getContext('2d');
  if (!drawing) throw new Error('Your browser could not create a working preview.');
  const probe = document.createElement('span');
  document.body.append(probe);
  const colour = (className: string) => {
    probe.className = className;
    return getComputedStyle(probe).color;
  };
  try {
    drawing.fillStyle = colour('text-canvas-pure');
    drawing.fillRect(0, 0, canvas.width, canvas.height);
    if (content.background !== 'blank') {
      drawing.strokeStyle = colour('text-hairline');
      drawing.fillStyle = drawing.strokeStyle;
      drawing.lineWidth = 1;
      if (content.background === 'grid') {
        drawing.beginPath();
        for (let x = 25; x < canvas.width; x += 25) {
          drawing.moveTo(x, 0);
          drawing.lineTo(x, canvas.height);
        }
        for (let y = 25; y < canvas.height; y += 25) {
          drawing.moveTo(0, y);
          drawing.lineTo(canvas.width, y);
        }
        drawing.stroke();
      } else {
        for (let x = 25; x < canvas.width; x += 25)
          for (let y = 25; y < canvas.height; y += 25) {
            drawing.beginPath();
            drawing.arc(x, y, 1, 0, Math.PI * 2);
            drawing.fill();
          }
      }
    }
    const colours = {
      ink: colour('text-ink'),
      sky: colour('text-brand-sky'),
      coral: colour('text-brand-coral'),
    };
    for (const stroke of content.strokes) {
      drawing.fillStyle = colours[stroke.colour];
      drawing.fill(new Path2D(inkPath(stroke)));
    }
    const image = canvas.toDataURL('image/png').split(',')[1];
    if (!image || image.length > 2_666_668)
      throw new Error('This page is too large for Tutor. Check a smaller working page.');
    return image;
  } finally {
    probe.remove();
  }
}
